import { Pagination } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useIndividualCustomerStore from "@/stores/useIndividualCustomerStore";

import CustomerDetailSheet from "@/components/individualCustomers/customerDetailSheet";
import IndividualCustomerFilterBar from "@/components/individualCustomers/IndividualCustomerFilterBar";
import RemindWarrantySheet from "@/components/individualCustomers/remindWarrantySheet";
import EmptyTable from "@/components/shared/EmptyTable";
import PageSizeSelect from "@/components/shared/pageSizeSelect";

import CheckRole from "@/components/shared/checkRole";

import { ArrowDown, ArrowUp, ArrowUpDown, Bell, MoreHorizontal } from "lucide-react";

// Sortable Table Head Component
const SortableTableHead = ({ field, children, sort, onSort }) => {
  const currentSort = sort.find((s) => s.field === field);

  return (
    <TableHead
      className="cursor-pointer select-none hover:bg-[#F9FAFC]"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {currentSort ? (
          currentSort.direction === "ASC" ? (
            <ArrowUp className="h-4 w-4 text-[#0049E6]" />
          ) : (
            <ArrowDown className="h-4 w-4 text-[#0049E6]" />
          )
        ) : (
          <ArrowUpDown className="h-4 w-4 text-[#9AA4B2]" />
        )}
      </div>
    </TableHead>
  );
};

// Get date part from "DD-MM-YYYY HH:mm" format
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const [datePart] = dateString.split(" ");
  return datePart || "-";
};

const IndividualCustomers = () => {
  const { t } = useTranslation();
  const [openRemindWarrantySheet, setOpenRemindWarrantySheet] = useState(false);
  const [openDetailSheet, setOpenDetailSheet] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const {
    customers,
    loading,
    filters,
    sort,
    getIndividuaCustomers,
    setFilters,
    toggleSort,
  } = useIndividualCustomerStore();

  useEffect(() => {
    getIndividuaCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPageChange = (page) => {
    setFilters({ ...filters, page });
    getIndividuaCustomers();
  };

  const onPageSizeChange = (size) => {
    localStorage.setItem("pageSize", size);
    setFilters({ ...filters, size, page: 1 });
    getIndividuaCustomers();
  };

  const onClickItem = (e, id) => {
    e.stopPropagation();
    setSelectedCustomerId(id);
    setOpenDetailSheet(true);
  };

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <div className="rounded-lg">
        <div className="flex flex-col rounded-lg bg-white">
          {/* Filter Bar */}
          <IndividualCustomerFilterBar />

          {/* Results Count */}
          {filters.totalElements > 0 && (
            <div className="border-b px-6 py-2 text-sm text-[#677389]">
              {filters.totalElements} {t("clientsPage.customersFound", "müşteri bulundu")}
            </div>
          )}

          {loading ? (
            <>
              {/* Masaustu iskeleti */}
              <div className="hidden md:block">
                <Table>
                  <TableSkeleton colSpan={5} />
                </Table>
              </div>
              {/* Mobil iskeleti — 5 sutunluk matris degil, yigili kartlar */}
              <div className="flex flex-col divide-y divide-gray-100 md:hidden">
                {Array.from({ length: 6 }).map((_, i) => (
                  <MusteriKartiIskelet key={i} />
                ))}
              </div>
            </>
          ) : customers.length ? (
            <>
            {/*
              EPS-5120 (8. bulgu) — liste telefonda masaustu tablosunu aynen
              gosteriyordu. Replikada olculdu (390 px): 5 sutundan yalniz 2'si
              ekranda, Telefon / Kayit Tarihi / Islemler sagda kaliyor, tablo
              325 px yana kaydirilabiliyor. Sayfa bozulmuyor ama ilk bakista
              bilgi gorunmuyor.

              Urunler sayfasindaki calisan desen uygulandi: masaustunde tablo
              aynen kalir, mobilde kart listesi.
            */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <SortableTableHead
                  field="firstName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("common.customer")}
                </SortableTableHead>
                <SortableTableHead
                  field="email"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("common.email")}
                </SortableTableHead>
                <SortableTableHead
                  field="phone"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("common.phone")}
                </SortableTableHead>
                <SortableTableHead
                  field="createdAt"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t("common.createdAt", "Kayıt Tarihi")}
                </SortableTableHead>
                <TableHead className="w-[80px]">{t("common.actions")}</TableHead>
              </TableHeader>
              <TableBody>
                {customers?.map((customer) => (
                  <TableRow
                    role="button"
                    onClick={(e) => onClickItem(e, customer.id)}
                    key={customer.id}
                    className="cursor-pointer hover:bg-[#F9FAFC]"
                  >
                    <TableCell className="font-medium">
                      {customer.firstName} {customer.lastName}
                    </TableCell>
                    <TableCell className="text-[#677389]">
                      {customer.email || "-"}
                    </TableCell>
                    <TableCell className="text-[#677389]">
                      {customer.phone || "-"}
                    </TableCell>
                    <TableCell className="text-[#677389]">
                      {formatDate(customer.createdAt)}
                    </TableCell>
                    <TableCell>
                      <CheckRole section="CLIENTS_INDIVIDUAL" action="EDIT">
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            onClick={(e) => e.stopPropagation()}
                            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-[#F3F4F6]"
                          >
                            <MoreHorizontal className="h-4 w-4 text-[#677389]" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClientId(customer.id);
                                setOpenRemindWarrantySheet(true);
                              }}
                              className="flex cursor-pointer items-center gap-2"
                            >
                              <Bell className="h-4 w-4" />
                              {t("clientsPage.remindWarranty")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </CheckRole>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            {/* Mobil kart listesi */}
            <div className="flex flex-col divide-y divide-gray-100 md:hidden">
              {customers?.map((customer) => (
                <MusteriKartiMobil
                  key={customer.id}
                  customer={customer}
                  formatDate={formatDate}
                  onClick={(e) => onClickItem(e, customer.id)}
                  onRemindWarranty={() => {
                    setSelectedClientId(customer.id);
                    setOpenRemindWarrantySheet(true);
                  }}
                  t={t}
                />
              ))}
            </div>
            </>
          ) : (
            <EmptyTable
              title={t("clientsPage.clientCouldNotFound")}
              desc={t("clientsPage.clientCouldNotFoundDesc")}
            />
          )}
          <div className="flex flex-col items-center border-t py-4">
            {filters.totalPages > 1 && (
              <Pagination
                totalPages={filters.totalPages}
                currentPage={filters.page}
                pageSize={filters.size}
                onPageChange={(page) => onPageChange(page)}
              />
            )}
            <PageSizeSelect
              onPageSizeChange={onPageSizeChange}
              value={filters.size}
            />
          </div>
        </div>
      </div>
      <RemindWarrantySheet
        open={openRemindWarrantySheet}
        onOpenChange={setOpenRemindWarrantySheet}
        selectedClientId={selectedClientId}
      />
      <CustomerDetailSheet
        open={openDetailSheet}
        onOpenChange={setOpenDetailSheet}
        customerId={selectedCustomerId}
      />
    </main>
  );
};

/**
 * EPS-5120 (8. bulgu) — Bireysel Musteriler listesinin mobil kart gorunumu.
 *
 * Tabloda 5 sutun var ve 390 px'lik ekranda yalniz 2'si goruluyordu. Kartta
 * tablonun TUM alanlari yer aliyor: ad soyad (baslik), e-posta, telefon ve
 * kayit tarihi; satirdaki "..." menusu de kartin sag ustune tasindi.
 *
 * Desen Urunler sayfasindaki `ProductCardMobile` ile ayni tutuldu.
 */
const MusteriKartiMobil = ({
  customer,
  formatDate,
  onClick,
  onRemindWarranty,
  t,
}) => (
  <div
    role="button"
    onClick={onClick}
    className="flex cursor-pointer items-start justify-between gap-3 px-4 py-4 active:bg-[#F9FAFC]"
  >
    <div className="min-w-0 flex-1">
      <div className="truncate font-medium text-[#111729]">
        {customer.firstName} {customer.lastName}
      </div>
      <dl className="mt-1 space-y-0.5 text-sm text-[#677389]">
        <div className="flex gap-2">
          <dt className="shrink-0">{t("common.email")}:</dt>
          <dd className="truncate">{customer.email || "-"}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0">{t("common.phone")}:</dt>
          <dd className="truncate">{customer.phone || "-"}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0">
            {t("common.createdAt", "Kayıt Tarihi")}:
          </dt>
          <dd className="truncate">{formatDate(customer.createdAt)}</dd>
        </div>
      </dl>
    </div>

    <CheckRole section="CLIENTS_INDIVIDUAL" action="EDIT">
      <DropdownMenu>
        <DropdownMenuTrigger
          onClick={(e) => e.stopPropagation()}
          aria-label={t("common.actions")}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-[#F3F4F6]"
        >
          <MoreHorizontal className="h-4 w-4 text-[#677389]" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onRemindWarranty();
            }}
            className="flex cursor-pointer items-center gap-2"
          >
            <Bell className="h-4 w-4" />
            {t("clientsPage.remindWarranty")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </CheckRole>
  </div>
);

/** Kart yuksekligiyle ayni iskelet — yukleme sirasinda zipplama olmasin. */
const MusteriKartiIskelet = () => (
  <div className="flex items-start justify-between gap-3 px-4 py-4">
    <div className="min-w-0 flex-1 space-y-2">
      <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
      <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
      <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
    </div>
    <div className="h-11 w-11 shrink-0 animate-pulse rounded-md bg-gray-100" />
  </div>
);

export default IndividualCustomers;
